import React, { useState, useEffect } from 'react';
import { Container, Card, Form, Button, Alert, Spinner, Row, Col } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, ShieldCheck, UserPlus, ArrowLeft, Globe, Fingerprint } from 'lucide-react';
import api from '../api/api';

const ClassroomRegistrationPage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    // Updated State to include external_id
    const [formData, setFormData] = useState({
        slug: searchParams.get('slug') || '',
        email: '',
        first_name: '',
        last_name: '',
        external_id: '', // New field for University ID
        password: '',
        confirm_password: ''
    });
    
    const [branding, setBranding] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    // Fetch Branding when slug is entered
    useEffect(() => {
        if (formData.slug.length >= 3) {
            const fetchBranding = async () => {
                try {
                    const res = await api.get(`/public/tenant-branding/${formData.slug}`);
                    setBranding(res.data);
                } catch (e) {
                    setBranding(null);
                }
            };
            fetchBranding();
        }
    }, [formData.slug]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        
        if (formData.password !== formData.confirm_password) {
            setError("Passwords do not match.");
            return;
        }

        setLoading(true);
        try {
            // Updated payload to include external_id and default role
            await api.post("/public/classroom/verify-initiate", {
                email: formData.email,
                slug: formData.slug,
                first_name: formData.first_name,
                last_name: formData.last_name,
                external_id: formData.external_id,
                role: 'faculty', 
                password: formData.password
            });
            setSuccess(true);
        } catch (err) {
            setError(err.response?.data?.detail || "Registration failed. Check your institutional domain rules.");
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <Container className="vh-100 d-flex align-items-center justify-content-center">
                <Card className="shadow-lg border-0 text-center p-5 rounded-4" style={{ maxWidth: '500px' }}>
                    <div className="bg-success bg-opacity-10 p-3 rounded-circle d-inline-block mx-auto mb-4">
                        <Mail size={40} className="text-success" />
                    </div>
                    <h3 className="fw-bold">Check your email</h3>
                    <p className="text-muted">
                        We have sent a verification link to <strong>{formData.email}</strong>. 
                        Please click the link to activate your faculty account for <strong>{branding?.name || formData.slug.toUpperCase()}</strong>.
                    </p>
                    <Button variant="primary" className="rounded-pill px-4 mt-3" onClick={() => navigate('/classroom-login')}>
                        Return to Login
                    </Button>
                </Card>
            </Container>
        );
    }

    return (
        <Container fluid className="vh-100 d-flex align-items-center justify-content-center bg-light">
            <Card className="shadow-lg border-0 overflow-hidden rounded-4" style={{ maxWidth: '900px' }}>
                <Row className="g-0">
                    <Col md={5} className="bg-primary p-5 text-white d-flex flex-column justify-content-center">
                        <div className="mb-4">
                            <Button variant="link" className="text-white p-0 d-flex align-items-center gap-2 text-decoration-none mb-4 opacity-75" onClick={() => navigate(-1)}>
                                <ArrowLeft size={16} /> Back
                            </Button>
                            {branding?.logo ? (
                                <img src={branding.logo} alt="Logo" height="60" className="mb-3" />
                            ) : (
                                <UserPlus size={50} className="mb-3" />
                            )}
                            <h2 className="fw-bold">{branding?.name || "AcademicOS"}</h2>
                            <p className="opacity-75">Faculty Onboarding & Verification</p>
                        </div>
                        <div className="small">
                            <div className="d-flex align-items-center gap-3 mb-3">
                                <ShieldCheck size={20} className="text-warning" />
                                <span>Grounded Institutional Intelligence</span>
                            </div>
                            <div className="d-flex align-items-center gap-3 mb-3">
                                <Globe size={20} className="text-warning" />
                                <span>Verified Academic Roster</span>
                            </div>
                        </div>
                    </Col>

                    <Col md={7} className="bg-white p-5">
                        <h4 className="fw-bold mb-4">Request Faculty Access</h4>
                        {error && <Alert variant="danger" className="py-2 small" role="alert">{error}</Alert>}
                        
                        <Form onSubmit={handleSubmit}>
                            <Form.Group className="mb-3">
                                <Form.Label className="x-small fw-bold text-muted text-uppercase">University Slug</Form.Label>
                                <Form.Control 
                                    placeholder="e.g. tau-gy" 
                                    value={formData.slug}
                                    onChange={(e) => setFormData({...formData, slug: e.target.value.toLowerCase()})}
                                    required 
                                    className="bg-light border-0"
                                />
                            </Form.Group>

                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="x-small fw-bold text-muted text-uppercase">First Name</Form.Label>
                                        <Form.Control 
                                            value={formData.first_name}
                                            onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                                            required className="bg-light border-0"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="x-small fw-bold text-muted text-uppercase">Last Name</Form.Label>
                                        <Form.Control 
                                            value={formData.last_name}
                                            onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                                            required className="bg-light border-0"
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Form.Group className="mb-3">
                                <Form.Label className="x-small fw-bold text-muted text-uppercase">Institutional Email</Form.Label>
                                <Form.Control 
                                    type="email" 
                                    placeholder="professor@university.edu"
                                    value={formData.email}
                                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                                    required className="bg-light border-0"
                                />
                            </Form.Group>

                            {/* NEW: External ID Field */}
                            <Form.Group className="mb-3">
                                <Form.Label className="x-small fw-bold text-muted text-uppercase d-flex align-items-center gap-2">
                                   <Fingerprint size={14} /> University Employee / Faculty ID
                                </Form.Label>
                                <Form.Control 
                                    placeholder="e.g. EMP-12345"
                                    value={formData.external_id}
                                    onChange={(e) => setFormData({...formData, external_id: e.target.value})}
                                    required className="bg-light border-0"
                                />
                                <Form.Text className="text-muted x-small">This helps us map your account to your courses.</Form.Text>
                            </Form.Group>

                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-4">
                                        <Form.Label className="x-small fw-bold text-muted text-uppercase">Password</Form.Label>
                                        <Form.Control 
                                            type="password"
                                            value={formData.password}
                                            onChange={(e) => setFormData({...formData, password: e.target.value})}
                                            required className="bg-light border-0"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-4">
                                        <Form.Label className="x-small fw-bold text-muted text-uppercase">Confirm Password</Form.Label>
                                        <Form.Control 
                                            type="password"
                                            value={formData.confirm_password}
                                            onChange={(e) => setFormData({...formData, confirm_password: e.target.value})}
                                            required className="bg-light border-0"
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Button variant="primary" type="submit" className="w-100 py-3 fw-bold rounded-pill shadow-sm" disabled={loading}>
                                {loading ? <Spinner size="sm" aria-hidden="true" /> : 'Initiate Verification'}
                            </Button>
                        </Form>
                    </Col>
                </Row>
            </Card>
        </Container>
    );
};

export default ClassroomRegistrationPage;